import * as React from "react";
import type {
  QueryBarHandle,
  QueryBarInputAriaProps,
  QueryBarSelection,
} from "../../components/QueryBar";
import { filterSkillsByQuery } from "../catalog";
import {
  findActiveSkillSlashToken,
  getResolvedSkillTokenDeletion,
  getSkillSlashOptionId,
  parseResolvedSkillTokensFromPrompt,
  toSelectedSkillReference,
  type ActiveSkillSlashToken,
} from "../selection";
import { MAX_SELECTED_SKILLS, type SelectedSkillReference, type SkillSummary } from "../types";

const MAX_SLASH_SUGGESTIONS = 4;

export interface UseSkillSlashControllerOptions {
  value: string;
  selection: QueryBarSelection;
  skills: readonly SkillSummary[];
  selectedSkills: readonly SelectedSkillReference[];
  replaceRange: QueryBarHandle["replaceRange"];
  focus?: QueryBarHandle["focus"];
  disabled?: boolean;
  listboxId?: string;
}

export interface SkillSlashController {
  open: boolean;
  atLimit: boolean;
  query: string;
  token: ActiveSkillSlashToken | null;
  suggestions: SkillSummary[];
  activeIndex: number;
  listboxId: string;
  inputAriaProps: QueryBarInputAriaProps;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onActiveIndexChange: (index: number) => void;
  selectSkill: (skill: SkillSummary) => SelectedSkillReference | null;
  dismiss: () => void;
}

export function useSkillSlashController({
  value,
  selection,
  skills,
  selectedSkills,
  replaceRange,
  focus,
  disabled = false,
  listboxId: providedListboxId,
}: UseSkillSlashControllerOptions): SkillSlashController {
  const generatedId = React.useId();
  const listboxId = providedListboxId ?? `skill-slash-${generatedId.replace(/:/g, "")}`;
  const token = findActiveSkillSlashToken(value, selection.start, selection.end);
  const tokenKey = token ? `${token.start}:${token.end}:${token.caret}:${token.value}` : null;
  const [dismissedTokenKey, setDismissedTokenKey] = React.useState<string | null>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [slashSession, setSlashSession] = React.useState({
    value,
    active: Boolean(token),
  });
  // Open only after the draft itself changes (typing `/`, filtering, paste).
  // Caret moves into an existing `/slug` — click, arrows — must not arm the picker.
  let slashSessionActive = slashSession.active;
  if (slashSession.value !== value) {
    slashSessionActive = Boolean(token);
    setSlashSession({ value, active: slashSessionActive });
  } else if (!token && slashSessionActive) {
    slashSessionActive = false;
    setSlashSession({ value, active: false });
  }
  // The token under the caret already counts as selected, so exempt it from the
  // "hide what is picked" rules. Typing a slug in full then confirms the match
  // instead of emptying the menu.
  const activeTokenSkill =
    (token &&
      parseResolvedSkillTokensFromPrompt(value, skills).find(
        ({ start, end }) => start === token.start && end === token.end
      )?.skill) ||
    null;
  const selectedIds = new Set(
    selectedSkills
      .filter((skill) => skill.id !== activeTokenSkill?.id)
      .map((skill) => skill.id)
  );
  const selectedSlugs = new Set(
    selectedSkills
      .filter((skill) => skill.slug !== activeTokenSkill?.slug)
      .map((skill) => skill.slug)
  );
  const selectionAtLimit =
    selectedSkills.filter((skill) => skill.id !== activeTokenSkill?.id).length >=
    MAX_SELECTED_SKILLS;
  const atLimitNotice = Boolean(
    slashSessionActive && token && !disabled && selectionAtLimit
  );
  const suggestions =
    token && !selectionAtLimit
      ? filterSkillsByQuery(skills, token.fragment)
          .filter(
            (skill) => skill.enabled && !selectedIds.has(skill.id) && !selectedSlugs.has(skill.slug)
          )
          .slice(0, MAX_SLASH_SUGGESTIONS)
      : [];
  const suggestionsKey = suggestions.map((skill) => skill.id).join("|");
  const open =
    slashSessionActive &&
    Boolean(token && !disabled && tokenKey !== dismissedTokenKey) &&
    (atLimitNotice || suggestions.length > 0);
  const safeActiveIndex =
    suggestions.length === 0 ? -1 : Math.min(Math.max(activeIndex, 0), suggestions.length - 1);

  React.useEffect(() => {
    setActiveIndex(suggestions.length > 0 ? 0 : -1);
  }, [suggestions.length, suggestionsKey, tokenKey]);

  React.useEffect(() => {
    if (dismissedTokenKey && dismissedTokenKey !== tokenKey) {
      setDismissedTokenKey(null);
    }
  }, [dismissedTokenKey, tokenKey]);

  const dismiss = React.useCallback(() => {
    setDismissedTokenKey(tokenKey);
    setSlashSession((current) => ({ ...current, active: false }));
  }, [tokenKey]);

  const selectSkill = React.useCallback(
    (skill: SkillSummary): SelectedSkillReference | null => {
      if (
        !token ||
        disabled ||
        !skill.enabled ||
        selectionAtLimit ||
        selectedIds.has(skill.id) ||
        selectedSlugs.has(skill.slug)
      ) {
        return null;
      }

      const reference = toSelectedSkillReference(skill);
      setDismissedTokenKey(tokenKey);
      replaceRange(`/${skill.slug} `, token.start, token.end, {
        focus: true,
        selectionMode: "end",
      });
      window.setTimeout(() => focus?.(), 0);
      return reference;
    },
    [
      disabled,
      focus,
      replaceRange,
      selectedIds,
      selectedSlugs,
      selectionAtLimit,
      token,
      tokenKey,
    ]
  );

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        (event.key === "Backspace" || event.key === "Delete") &&
        !event.nativeEvent.isComposing &&
        !event.altKey &&
        !event.metaKey &&
        !event.ctrlKey
      ) {
        const selectionStart = event.currentTarget?.selectionStart ?? selection.start;
        const selectionEnd = event.currentTarget?.selectionEnd ?? selection.end;
        if (selectionStart === selectionEnd) {
          const direction = event.key === "Delete" ? "forward" : "backward";
          const deletion = getResolvedSkillTokenDeletion(
            value,
            selectionStart,
            skills,
            direction
          );

          if (deletion) {
            const activeToken = findActiveSkillSlashToken(
              value,
              selectionStart,
              selectionEnd
            );
            const activeTokenIsExactResolvedSlug =
              !activeToken ||
              parseResolvedSkillTokensFromPrompt(value, skills).some(
                ({ start, end, skill }) =>
                  start === activeToken.start &&
                  end === activeToken.end &&
                  skill.slug === activeToken.fragment
              );

            if (activeTokenIsExactResolvedSlug) {
              event.preventDefault();
              replaceRange("", deletion.start, deletion.end, {
                focus: true,
                selectionMode: "start",
              });
              return;
            }
          }
        }
      }

      if (!open) return;

      if (event.key === "Escape") {
        event.preventDefault();
        dismiss();
        return;
      }
      if (atLimitNotice) {
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (suggestions.length > 0) {
          setActiveIndex(safeActiveIndex < 0 ? 0 : (safeActiveIndex + 1) % suggestions.length);
        }
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (suggestions.length > 0) {
          setActiveIndex(safeActiveIndex <= 0 ? suggestions.length - 1 : safeActiveIndex - 1);
        }
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        if (suggestions.length > 0) setActiveIndex(0);
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        if (suggestions.length > 0) {
          setActiveIndex(suggestions.length - 1);
        }
        return;
      }
      if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        const activeSkill = suggestions[safeActiveIndex];
        if (activeSkill) selectSkill(activeSkill);
      }
    },
    [
      atLimitNotice,
      dismiss,
      open,
      replaceRange,
      safeActiveIndex,
      selection,
      selectSkill,
      skills,
      suggestions,
      value,
    ]
  );

  const activeSkill = suggestions[safeActiveIndex];
  const hasListboxOptions = open && !atLimitNotice && Boolean(activeSkill);
  const inputAriaProps: QueryBarInputAriaProps = {
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-haspopup": "listbox",
    "aria-controls": hasListboxOptions ? listboxId : undefined,
    "aria-expanded": open,
    "aria-activedescendant":
      hasListboxOptions && activeSkill
        ? getSkillSlashOptionId(listboxId, activeSkill.id)
        : undefined,
  };

  return {
    open,
    atLimit: atLimitNotice,
    query: token?.fragment ?? "",
    token,
    suggestions,
    activeIndex: safeActiveIndex,
    listboxId,
    inputAriaProps,
    onKeyDown,
    onActiveIndexChange: setActiveIndex,
    selectSkill,
    dismiss,
  };
}
